/**
 * 侧边栏（架构 §2 src/components/layout/Sidebar.tsx / T12）
 *  - 导航：聊天 / 用户中心 / 邀请码管理（创始人可见）
 *  - 底部：当前账号 + 退出
 */
import { NavLink, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import LogoutIcon from '@mui/icons-material/Logout';
import Typography from '@mui/material/Typography';
import { useAuthStore } from '../../store/auth.store';
import { useChatStore } from '../../store/chat.store';
import { useUserStore } from '../../store/user.store';
import UserAvatar from '../common/UserAvatar';

export default function Sidebar({
  mobileOpen = false,
  onClose,
  isMobile = false,
}: {
  mobileOpen?: boolean;
  onClose?: () => void;
  isMobile?: boolean;
}) {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const isFounder = useAuthStore((s) => s.user?.role === 'founder');
  const logout = useAuthStore((s) => s.logout);
  const resetChat = useChatStore((s) => s.reset);
  const resetUser = useUserStore((s) => s.reset);

  function handleLogout() {
    logout();
    resetChat();
    resetUser();
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('zhihui:auth-expired-clear'));
    navigate('/login', { replace: true });
  }

  const item = (to: string, label: string, icon: React.ReactNode, show = true) => {
    if (!show) return null;
    return (
      <ListItemButton
        component={NavLink}
        to={to}
        key={to}
        onClick={() => onClose?.()}
        sx={{
          borderRadius: 2,
          mx: 1,
          '&.active': { bgcolor: 'primary.main', color: '#fff', '& .MuiListItemIcon-root': { color: '#fff' } },
        }}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>{icon}</ListItemIcon>
        <ListItemText primary={label} />
      </ListItemButton>
    );
  };

  const content = (
    <Box
      sx={{
        width: 200,
        flexShrink: 0,
        bgcolor: 'background.paper',
        borderRight: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box sx={{ p: 2, fontWeight: 700, fontSize: 18, color: 'primary.main' }}>知会 ZhiHui</Box>
      <List sx={{ flex: 1, py: 0.5 }}>
        {item('/', '聊天', <ChatBubbleOutlineIcon />)}
        {item('/user', '用户中心', <PersonOutlineIcon />)}
        {item('/invites', '邀请码管理', <ConfirmationNumberIcon />, isFounder)}
      </List>
      <Divider />
      <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <UserAvatar user={user} size={32} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap>
            {user?.username}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {user?.role === 'founder' ? '创始人' : '成员'}
          </Typography>
        </Box>
        <ListItemButton
          onClick={handleLogout}
          sx={{ borderRadius: 2, minWidth: 0, p: 1 }}
          title="退出登录"
          disabled={!token}
        >
          <LogoutIcon fontSize="small" />
        </ListItemButton>
      </Box>
    </Box>
  );

  // BUG-007 修复：手机端（≤768px）侧栏改为可切换抽屉，释放主区全宽
  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{ '& .MuiDrawer-paper': { width: 240, boxSizing: 'border-box' } }}
      >
        {content}
      </Drawer>
    );
  }
  return content;
}
